// WO-GF-001-P1B-S7 (W5 / P0) — the real per-mountain destinations for the hub HUD nav + the non-Whistler
// body "section nav". Live-derived (production snowbus.com, 2026-07-21 read-only walk): the mode navigation
// routes to concrete, build-served pages. Both the componentized header (SiteHeaderHub, on the 30 landings)
// and the document-scoped HubChromeLayer (on the ~361 sub-pages) resolve destinations through THIS map so
// the nav is identical everywhere and never links to a page the build does not serve (no dangling — the
// funnel-precedent rule: wire to what the build actually serves).
//
// LIVE EVIDENCE (clicked on live, URL observed):
//   • hub hero "market place" tile      → /mountain-<code>/deals-and-packages
//   • hub hero "directory"  tile        → /mountain-<code>/profile
//   • hub hero "transport"  tile        → /mountain-<code> (hub = the buses landing; the hub's default mode)
//   • body "Transport information" btn  → /mountain-<code>/bus-schedule
//   • body "Mountain profile"      btn  → /mountain-<code>/profile
//   • body "Rentals"               btn  → /mountain-<code>/equipment-rentals  (NOT served off-Whistler)
//   • body "Activities"            btn  → /mountain-<code>/activities
//   • body "Whistler is live…"     btn  → /mountain-whistler
//
// ⚠ WO-GF-001-P1B-S12 · RW2-1 — FOUNDER RULING (a), 2026-07-22, OVERRIDES the live-derived destination above
// and the S7 reasoning that follows it. Verbatim: "selecting Transport after a mountain select goes to the
// MOUNTAIN PAGE (/mountain-<mountain>), never the schedule page, never the directory."
//
// WHY the founder is right about the destination (observed 2026-07-22, build /mountain-whistler @1440):
// the mountain hub IS the transport surface — its HUD renders TRANSPORT as the ACTIVE mode and the page
// carries the real transport search widget (data-transport-source="db"). /bus-schedule, by contrast, is a
// DIRECTORY page: it renders the leftDock "Select Research Category" dock (see §S8 category-nav below, which
// lists bus-schedule among the directory pages). That is precisely the founder's second symptom — "another
// Transport click lands in the directory" — the schedule page READS as the directory because it is one.
//
// SCOPE of the ruling = the MODE selectors ("Transport" as a mode, chosen after/with a mountain):
//   • HUD "transport" tile          → /mountain-<code>   (this function, below)
//   • landing funnel TRANSPORT mode → /mountain-<code>   (LandingFunnelLayer destFor)
//   • hamburger "Book Travel"       → rides the funnel, so it inherits the fix (no separate wiring)
// NOT in scope — controls live labels as INFORMATION/COMPANIES, which are a different control answering a
// different question, and whose only nav path to the schedule page this is. Repointing them would leave
// /bus-schedule unreachable from the nav graph (a new orphan cluster) and destroy the transport-information
// capability the port is required to carry:
//   • body section-nav "TRANSPORT INFORMATION"  → /bus-schedule (sectionNavHref, unchanged)
//   • directory category "TRANSPORT COMPANIES"  → /bus-schedule (categoryNavHref, unchanged)
//   • footer route quicklinks / FAQ text links  → /bus-schedule (unchanged)
// This mode-vs-information split is the one interpretation call in RW2-1; it is flagged in the §S12 close
// for the founder's re-walk rather than decided silently.
//
// "Rentals" resolves
// to /ski-rentals for non-Whistler (the served rentals page; live's /equipment-rentals index exists only for
// Whistler), and to /equipment-rentals for Whistler.

export type HubMode = "marketplace" | "transport" | "directory";

// THE mountain hub for a mountain code — the one place that spells the hub route. Every caller that means
// "take me to this mountain" resolves through here (the HUD transport mode per RW2-1, and the header
// change-mountain dropdown per S15 · F89), so the two can never drift into two different hub URLs.
export function mountainHubHref(code: string): string {
  return `/mountain-${code}`;
}

// header HUD nav (marketplace / transport / directory). flights/stays stay disabled (Coming Soon) → no href.
export function hudNavHref(code: string, label: string): string | null {
  switch (label.trim().toLowerCase()) {
    case "marketplace":
    case "market place":
      return `/mountain-${code}/deals-and-packages`;
    case "transport":
      // RW2-1 · founder ruling (a) 2026-07-22 — the mountain hub, NOT /bus-schedule (see header note).
      return mountainHubHref(code);
    case "directory":
    // ── WO-SITE-REAL-001 · P1b — THE DESIGN'S OWN LABELS, ADDED AS ALIASES ────────────────────────
    // FOUND BY THE ROW-BY-ROW AUDIT (tooling/port-harness/control-url-audit.mjs), which the founder
    // ordered after finding one bad row by hand: 12 of 14 controls resolved on all 30 mountains, and
    // these two resolved on NONE. `hudNavHref` answers "directory", but the header renders the DC's
    // PAGES labels — "Research" and "Home & mountain" — so 60 rows (30 mountains x 2 controls) hit
    // `default: return null` and produced controls that went nowhere at all.
    //
    // ALIASED RATHER THAN RENAMED, in both directions: the DC's wording is the founder's design copy
    // and is not ours to change, and the existing "directory" key is what the ported HUD already
    // passes. Changing either side would have fixed one surface and broken the other.
    //
    // "Home & mountain" is the design's `mtn-home` — the mountain hub, the same destination founder
    // ruling RW2-1 pins "Transport" to. Two labels, one honest destination; the alternative was
    // inventing a second "home" URL that the contract does not serve.
    //
    // FOUNDER RULING 2026-08-14 — AND NOW IT HAS ITS OWN URL, so the alias stops being a redirect to
    // a neighbouring section. `/mountain-<code>/profile` is the MOUNTAIN PROFILE (dCat "mountain");
    // sending the header's "Research" control there was the closest served path at the time, not the
    // right one, and it is why W1 read as "I still cannot select from the category drop down in
    // research" — the control landed on a category instead of on the section that owns the dropdown.
    // Both keys resolve here on purpose: "directory" is the mode key the ported HUD passes and
    // "Research" is the label the design renders for that same one control, so they must not diverge.
    case "research":
      return `/mountain-${code}/research`;
    case "home & mountain":
      return mountainHubHref(code);
    default:
      return null; // flights / stays — Coming Soon
  }
}

// non-Whistler body "section nav" (MOUNTAIN PROFILE / TRANSPORT INFORMATION / RENTALS / ACTIVITIES /
// WHISTLER IS LIVE — BOOK & SHOP). Whistler is the marketplace-live mountain, so its rentals index is
// /equipment-rentals; every other mountain serves /ski-rentals.
export function sectionNavHref(code: string, label: string): string | null {
  const t = label.trim().toLowerCase();
  if (t.startsWith("whistler is live")) return "/mountain-whistler";
  if (t === "mountain profile") return `/mountain-${code}/profile`;
  if (t === "transport information") return `/mountain-${code}/bus-schedule`;
  if (t === "rentals")
    return code === "whistler"
      ? `/mountain-${code}/equipment-rentals`
      : `/mountain-${code}/ski-rentals`;
  if (t === "activities") return `/mountain-${code}/activities`;
  return null;
}

// SNOWCLUB-LAUNCH-REMAINING-PARITY-001 (founder QA, 2026-09-21) — the mountain-profile page's OWN
// "Research categories" row (`dCats` in mountain-profile.data.json, extracted by
// tooling/port-harness/extract-profile.mjs from the DC's DIR_CATS: Transport / Activities / Rentals /
// Food & drink / Mountains / Airports, each carrying a count and the DC's own bg/color/opacity — i.e.
// data shaped for a clickable category nav). `MountainProfile.tsx` painted these as inert <span>s with
// no destination at all: visually a menu, actually dead — the "click is swallowed" class the founder
// reported (Transport read as a link, went nowhere, URL never left /profile). This is the ONE
// resolver for that whole row, so every category the row can name gets fixed at once rather than one
// label at a time.
//
// "Transport" needs its own line because the row says "Transport" where `sectionNavHref` expects
// "transport information" (the section-nav band's own wording); everything else reuses the mappings
// this file already ships so the profile row can never name a page those don't also serve.
// "Food & drink" stays without a destination — the same rule `researchHref` states in the export
// (sg-beta-v2.01.html): no category feed is published for it, so nothing is invented for it here
// either (the row already marks it dim, opacity 0.45, no count).
export function profileCategoryHref(code: string, label: string): string | null {
  const t = label.trim().toLowerCase();
  if (t === "transport") return `/mountain-${code}/bus-schedule`;
  if (t === "mountains") return `/mountain-${code}/profile`;
  if (t === "airports") return `/mountain-${code}/bus-schedule/airport-information`;
  if (t === "food & drink") return null;
  return sectionNavHref(code, label); // "activities" / "rentals" — shared with the section-nav band
}

// the mountain code out of a build pathname (`/mountain-<code>[/...]`), or null when not a mountain page.
export function mountainCodeFromPath(pathname: string): string | null {
  const m = /^\/mountain-([a-z0-9-]+?)(?:\/|$)/i.exec(pathname);
  return m ? (m[1] ?? null) : null;
}

// WO-GF-001-P1B-S8 (W3/W5 · P2) — the leftDock "Select Research Category" DIRECTORY dropdown. Live-derived
// (production snowbus.com, 2026-07-21 read-only walk): on every directory page (profile / activities /
// bus-schedule / equipment-rentals + their detail/leaf depth) the leftDock `.dropdown-toggle.btn.btn-select`
// (labeled by the CURRENT category) opens a react-bootstrap menu of the 4 research categories; selecting one
// JS-navigates to that category's directory page for the current mountain. The mirror stripped that JS → the
// toggle is a dead photocopy (catalog #9/#12/#15/#21, 281 pp). The 4 destinations are exactly the section-nav
// set (same live behavior, same served pages), so this reuses that mapping → no new destinations, no dangling.
//
// LIVE EVIDENCE (clicked on live, URL observed 2026-07-21):
//   • MOUNTAINS                  → /mountain-<code>/profile
//   • TRANSPORT COMPANIES        → /mountain-<code>/bus-schedule
//   • ACTIVITIES & ENTERTAINMENT → /mountain-<code>/activities
//   • EQUIPMENT RENTAL COMPANIES → /mountain-<code>/equipment-rentals (Whistler; served) — off-Whistler live
//     serves /equipment-rentals too but the build mirrored only /ski-rentals there (F59 coverage gap), so the
//     option resolves to the served /ski-rentals off-Whistler (funnel-precedent, identical to section-nav
//     "Rentals"). Every option is a build-served page for every mountain → no-404.
export const CATEGORY_LABELS = [
  "MOUNTAINS",
  "TRANSPORT COMPANIES",
  "ACTIVITIES & ENTERTAINMENT",
  "EQUIPMENT RENTAL COMPANIES",
] as const;

// destination for a single category label (served page, per-mountain). Mirrors sectionNavHref exactly.
export function categoryNavHref(code: string, label: string): string | null {
  switch (label.trim().toUpperCase()) {
    case "MOUNTAINS":
      return `/mountain-${code}/profile`;
    case "TRANSPORT COMPANIES":
      return `/mountain-${code}/bus-schedule`;
    case "ACTIVITIES & ENTERTAINMENT":
      return `/mountain-${code}/activities`;
    case "EQUIPMENT RENTAL COMPANIES":
      return code === "whistler"
        ? `/mountain-${code}/equipment-rentals`
        : `/mountain-${code}/ski-rentals`;
    default:
      return null; // not a directory-category toggle (e.g. the transport widget's location selector)
  }
}

// WO-GF-001-P1B-S13 (RW3-6) — the HUD **DIRECTORY** button opens a MENU on live, it does not jump.
// The founder expected a category dropdown; live agrees, so no override was needed — this is a straight
// port, verified read-only on production 2026-07-22 (/mountain-whistler @1440):
//   click DIRECTORY ⇒ url UNCHANGED + a `MuiMenu-list` opens with exactly five rows, each of which
//   navigates (clicked one per page load, destination URL observed):
//     MOUNTAIN INFO        → /mountain-<code>/profile
//     ALL VENDORS          → /mountain-<code>/activities
//     ALL RENTALS          → /mountain-<code>/equipment-rentals
//     TRANSPORT OPERATORS  → /mountain-<code>/bus-schedule
//     AIRPORTS             → /mountain-<code>/bus-schedule/airport-information
// Off-Whistler, ALL RENTALS resolves to the SERVED /ski-rentals — the same F59 coverage-gap precedent
// sectionNavHref and categoryNavHref already apply (live serves /equipment-rentals for all 30 mountains;
// the build mirrored Whistler's only). Every row is a build-served page for every mountain → no-404.
export const DIRECTORY_MENU_LABELS = [
  "MOUNTAIN INFO",
  "ALL VENDORS",
  "ALL RENTALS",
  "TRANSPORT OPERATORS",
  "AIRPORTS",
] as const;

export function directoryMenuHref(code: string, label: string): string | null {
  switch (label.trim().toUpperCase()) {
    case "MOUNTAIN INFO":
      return `/mountain-${code}/profile`;
    case "ALL VENDORS":
      return `/mountain-${code}/activities`;
    case "ALL RENTALS":
      return code === "whistler"
        ? `/mountain-${code}/equipment-rentals`
        : `/mountain-${code}/ski-rentals`;
    case "TRANSPORT OPERATORS":
      return `/mountain-${code}/bus-schedule`;
    case "AIRPORTS":
      return `/mountain-${code}/bus-schedule/airport-information`;
    default:
      return null;
  }
}

export function directoryMenuOptions(
  code: string,
): Array<{ label: string; href: string }> {
  return DIRECTORY_MENU_LABELS.map((label) => ({
    label,
    href: directoryMenuHref(code, label)!,
  }));
}

// the full 4-option menu for a mountain — [label, href] pairs, all build-served. Used by the category-nav
// layer to render the dropdown's real <a href> options (crawlable → also closes the marketplace-depth orphan
// cluster where the option points at a served-but-orphaned index, e.g. Whistler /equipment-rentals).
export function categoryOptions(code: string): Array<{ label: string; href: string }> {
  return CATEGORY_LABELS.map((label) => ({
    label,
    href: categoryNavHref(code, label)!,
  }));
}
